// Dynamic filter-option sources for ListPage select filters (CPE-FE2).
//
// Some list filters target a relationship whose option set is not a static
// enum (e.g. "Designation") and so cannot be baked into the generated
// ListPageConfig as `options:`. Such filters declare
// `dynamicOptionsSource: "<key>"` in the entity-hint; the generator emits the
// key, and ListPage resolves the key against this registry at render time.
//
// Each source is a parameterless async loader returning the option list. The
// loader is invoked inside a react-query (keyed by the source name) so the
// fetch is cached + deduped across pages. Keep loaders cheap and bounded —
// these populate a <select>, so the realistic ceiling is ~hundreds of rows.
//
// This is the v2-native, config-driven analogue of the v1 dashboard's
// designation-filter dropdown (web/dashboard Events.tsx) — the backend
// `/events?designation_id=` filter is honoured end-to-end (CPE-020, F7).

import { designationsApi } from "../../api/designations";
import { campaignsApi } from "../../api/campaigns";

export interface FilterOption {
  value: string;
  label: string;
}

export type FilterOptionLoader = () => Promise<FilterOption[]>;

// Designation options — one entry per designation, parent-qualified for
// nested rows so operators can disambiguate same-named children. Mirrors the
// v1 designationNameMap projection (`parent > name`).
const loadDesignationOptions: FilterOptionLoader = async () => {
  const { items } = await designationsApi.listDesignations({ pageSize: 100 });
  return items.map((d) => ({
    value: d.id,
    label: d.parent_name ? `${d.parent_name} › ${d.name}` : d.name,
  }));
};

// DRIFT-1173 (CHG-3054) — Campaign options for the pledge-list `campaign_id`
// filter (PLG-R02 admin /pledges?campaign_id=). One entry per campaign;
// bounded to the same ~hundreds ceiling as designations.
const loadCampaignOptions: FilterOptionLoader = async () => {
  const { items } = await campaignsApi.listCampaigns({ pageSize: 100 });
  return items.map((c) => ({ value: c.id, label: c.name }));
};

export const FILTER_OPTION_SOURCES: Record<string, FilterOptionLoader> = {
  designations: loadDesignationOptions,
  campaigns: loadCampaignOptions,
};
